import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { LandOwner } from '@popety-io/types'
import { LandsSectionProps } from './types'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  ExpandableList,
  ExpandableItem,
  ExpandableHeader,
  ExpandableContent,
  ExpandableDetails,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataValue,
  DataValueWithUnit,
  DataUnit,
  PropertyBadge as Badge,
  OwnerCard,
  OwnerName,
  OwnerActions,
  OwnerActionBtn,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Icon } from '../../../atoms/Icon'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'

const DetailsLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.transitions.fast};

  svg {
    width: 12px;
    height: 12px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};

    svg {
      transform: translateX(2px);
    }
  }
`

export const LandsSection: React.FC<LandsSectionProps> = ({
  lands,
  detailsLink,
  landOwners: externalLandOwners,
  ownersLoading: externalOwnersLoading,
}) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const derivedLandOwners = useMemo<Record<string, LandOwner[]>>(() => {
    const map: Record<string, LandOwner[]> = {}
    if (!lands) return map
    lands.forEach((land: any) => {
      const key = land?.land_id || land?.id
      if (!key) return
      if (Array.isArray(land?.owners) && land.owners.length > 0) {
        map[key] = land.owners as LandOwner[]
      }
    })
    return map
  }, [lands])

  const landOwners =
    externalLandOwners && Object.keys(externalLandOwners).length > 0
      ? externalLandOwners
      : derivedLandOwners
  const ownersLoading = externalOwnersLoading || false

  const toggleExpand = (landId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(landId)) {
        newSet.delete(landId)
      } else {
        newSet.add(landId)
      }
      return newSet
    })
  }

  const handleGoogleSearch = (name: string) => {
    window.open(
      `https://www.google.com/search?q=${encodeURIComponent(name)}`,
      '_blank'
    )
  }

  const handleLinkedInSearch = (name: string) => {
    window.open(
      `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(name)}`,
      '_blank'
    )
  }

  const handleLocalSearch = (name: string) => {
    window.open(`https://www.local.ch/s/${encodeURIComponent(name)}`, '_blank')
  }

  const isCompany = (owner: LandOwner) => {
    return (
      owner.type === 'company' ||
      owner.type === 'institutional' ||
      (owner.name &&
        (owner.name.includes('SA') ||
          owner.name.includes('SARL') ||
          owner.name.includes('FONDATION') ||
          owner.name.includes('ASSOCIATION')))
    )
  }

  const getOwnerSummary = (landId: string) => {
    const owners = landOwners[landId]
    if (!owners || owners.length === 0) return null

    if (owners.length === 1) return owners[0].name
    if (owners.length === 2) return owners.map((o) => o.name).join(', ')

    return `${owners[0].name} +${owners.length - 1}`
  }

  if (!lands || lands.length === 0) {
    return null
  }

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.lands.title')}
        </SectionTitle>
        <SectionCount>{lands.length}</SectionCount>
      </SectionHeader>
      <SectionContent>
        <ExpandableList>
          {lands.map((land: any, index: number) => {
            const landId = land.id || land.egrid_nb || `land-${index}`
            const ownerKey = land.land_id || land.id
            const isExpanded = expandedItems.has(landId)
            const owners = ownerKey ? landOwners[ownerKey] : undefined
            const ownerSummary = ownerKey ? getOwnerSummary(ownerKey) : null

            return (
              <ExpandableItem key={landId} $expanded={isExpanded}>
                <ExpandableHeader onClick={() => toggleExpand(landId)}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: '500',
                          fontSize: '12px',
                          color: '#1A1A1A',
                        }}
                      >
                        {land.egrid_nb ||
                          t('propertyTooltip.lands.unknownEgrid')}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color:
                            land.cover_area_perc === 1
                              ? 'rgb(142, 209, 165)'
                              : 'red',
                          marginTop: '2px',
                        }}
                      >
                        {land.cover_area_perc
                          ? land.cover_area_perc * 100 + '%'
                          : ''}
                      </div>
                      {ownerSummary && (
                        <div
                          style={{
                            fontSize: '10px',
                            color: '#6B6B6B',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '180px',
                          }}
                        >
                          {ownerSummary}
                        </div>
                      )}
                    </div>
                    {land.area && (
                      <Badge>{formatNumberSwiss(land.area)} m²</Badge>
                    )}
                  </div>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#6B6B6B',
                      marginLeft: '8px',
                    }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </ExpandableHeader>
                <ExpandableContent $expanded={isExpanded}>
                  <ExpandableDetails>
                    <DataGrid>
                      {land.egrid_nb && (
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t('propertyTooltip.landInformation.egrid')}
                            explanation={tCommon(
                              'propertyExplanations.landInformation.egridBuilding'
                            )}
                          >
                            {t('propertyTooltip.landInformation.egrid')}
                          </DataLabelWithTooltip>
                          <DataValue>{land.egrid_nb}</DataValue>
                        </DataRow>
                      )}

                      {land.area && (
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t('propertyTooltip.landInformation.area')}
                            explanation={tCommon(
                              'propertyExplanations.landInformation.areaBuilding'
                            )}
                          >
                            {t('propertyTooltip.landInformation.area')}
                          </DataLabelWithTooltip>
                          <DataValueWithUnit>
                            <DataValue $large>
                              {formatNumberSwiss(land.area)}
                            </DataValue>
                            <DataUnit>m²</DataUnit>
                          </DataValueWithUnit>
                        </DataRow>
                      )}

                      <DataRow>
                        <DataLabelWithTooltip
                          label={t('propertyTooltip.owners.title')}
                          explanation={tCommon(
                            'propertyExplanations.owners.description'
                          )}
                        >
                          {t('propertyTooltip.owners.title')}
                        </DataLabelWithTooltip>
                        <DataValue />
                      </DataRow>
                      <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                        {ownersLoading ? (
                          <div
                            style={{
                              fontSize: '11px',
                              color: '#999',
                              fontStyle: 'italic',
                            }}
                          >
                            ...
                          </div>
                        ) : owners && owners.length > 0 ? (
                          owners.map((owner, idx) => (
                            <OwnerCard
                              key={`${owner.id}-${idx}`}
                              style={{
                                marginBottom:
                                  idx < owners.length - 1 ? '8px' : 0,
                              }}
                            >
                              <OwnerName>
                                {owner.name ||
                                  t('propertyTooltip.owners.unknown')}
                              </OwnerName>
                              <OwnerActions>
                                {isCompany(owner) ? (
                                  <OwnerActionBtn
                                    $variant="company"
                                    title={t('propertyTooltip.owners.company')}
                                    onClick={() =>
                                      owner.name &&
                                      handleGoogleSearch(owner.name)
                                    }
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                    >
                                      <path d="M18 15h-2v2h2m0-6h-2v2h2m2 6h-8v-2h2v-2h-2v-2h2v-2h-2V9h8M10 7H8V5h2m0 6H8V9h2m0 6H8v-2h2m0 6H8v-2h2M6 7H4V5h2m0 6H4V9h2m0 6H4v-2h2m0 6H4v-2h2m6-10V3H2v18h20V7H12z" />
                                    </svg>
                                  </OwnerActionBtn>
                                ) : (
                                  <>
                                    <OwnerActionBtn
                                      $variant="google"
                                      title={t(
                                        'propertyTooltip.owners.searchGoogle'
                                      )}
                                      onClick={() =>
                                        owner.name &&
                                        handleGoogleSearch(owner.name)
                                      }
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" />
                                      </svg>
                                    </OwnerActionBtn>
                                    <OwnerActionBtn
                                      $variant="linkedin"
                                      title={t(
                                        'propertyTooltip.owners.searchLinkedIn'
                                      )}
                                      onClick={() =>
                                        owner.name &&
                                        handleLinkedInSearch(owner.name)
                                      }
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                                      </svg>
                                    </OwnerActionBtn>
                                    <OwnerActionBtn
                                      $variant="local"
                                      title="local.ch"
                                      onClick={() =>
                                        owner.name &&
                                        handleLocalSearch(owner.name)
                                      }
                                    >
                                      <span>local</span>
                                    </OwnerActionBtn>
                                  </>
                                )}
                              </OwnerActions>
                            </OwnerCard>
                          ))
                        ) : (
                          <DataValue>
                            {t('propertyTooltip.owners.unknown')}
                          </DataValue>
                        )}
                      </div>

                      {land.municipality_name && (
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t(
                              'propertyTooltip.landInformation.municipality'
                            )}
                            explanation={tCommon(
                              'propertyExplanations.landInformation.municipality'
                            )}
                          >
                            {t('propertyTooltip.landInformation.municipality')}
                          </DataLabelWithTooltip>
                          <DataValue>{land.municipality_name}</DataValue>
                        </DataRow>
                      )}

                      {land.district_name && (
                        <DataRow>
                          <DataLabelWithTooltip
                            label={t(
                              'propertyTooltip.landInformation.district'
                            )}
                            explanation={tCommon(
                              'propertyExplanations.landInformation.district'
                            )}
                          >
                            {t('propertyTooltip.landInformation.district')}
                          </DataLabelWithTooltip>
                          <DataValue>{land.district_name}</DataValue>
                        </DataRow>
                      )}
                    </DataGrid>
                  </ExpandableDetails>
                </ExpandableContent>
              </ExpandableItem>
            )
          })}
        </ExpandableList>
        {detailsLink && (
          <DetailsLink href={detailsLink}>
            <Icon name="arrow-right" size="small" />
            {t('propertyTooltip.lands.viewDetails')}
          </DetailsLink>
        )}
      </SectionContent>
    </InfoSection>
  )
}

export default LandsSection
